/**
 * Локальный кэш списка чатов (localStorage + in-memory).
 * Паттерн stale-while-revalidate: при открытии /chat сразу показываем
 * закэшированные диалоги, параллельно обновляем с сервера.
 */

const CACHE_VERSION = 1;
const KEY_PREFIX = `chat_cache_v${CACHE_VERSION}_`;
/** После этого возраста данные всё ещё показываются, но считаются устаревшими. */
const STALE_MS = 30 * 60 * 1000;
/** Старше — не используем кэш вообще. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** @type {Map<string, object>} */
const memory = new Map();

function storageKey(userId) {
  return `${KEY_PREFIX}${userId}`;
}

/** Убираем тяжёлые base64-аватары — иначе localStorage быстро переполняется. */
function sanitizeAvatar(avatar) {
  if (!avatar || typeof avatar !== 'string') return avatar ?? null;
  if (avatar.startsWith('data:') && avatar.length > 200) return null;
  if (avatar.length > 500) return null;
  return avatar;
}

function sanitizeDialog(dialog) {
  if (!dialog || typeof dialog !== 'object') return dialog;
  return { ...dialog, avatar: sanitizeAvatar(dialog.avatar) };
}

function sanitizeUser(user) {
  if (!user || typeof user !== 'object') return user;
  return { ...user, avatar: sanitizeAvatar(user.avatar) };
}

function sanitizeForStorage(data) {
  return {
    userId: data.userId,
    updatedAt: data.updatedAt ?? Date.now(),
    dialogs: (data.dialogs ?? []).map(sanitizeDialog),
    groups: data.groups ?? [],
    channels: data.channels ?? [],
    users: (data.users ?? []).map(sanitizeUser),
    stickerPacks: data.stickerPacks ?? [],
    currentUser: data.currentUser
      ? { ...data.currentUser, avatar: sanitizeAvatar(data.currentUser.avatar) }
      : null,
  };
}

function readFromStorage(userId) {
  if (typeof window === 'undefined' || !userId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || String(parsed.userId) !== String(userId)) return null;

    const age = Date.now() - (parsed.updatedAt || 0);
    if (age > MAX_AGE_MS) {
      window.localStorage.removeItem(storageKey(userId));
      return null;
    }

    return { ...parsed, isStale: age > STALE_MS };
  } catch {
    return null;
  }
}

function writeToStorage(userId, data) {
  if (typeof window === 'undefined' || !userId) return;
  try {
    const payload = sanitizeForStorage({ ...data, userId, updatedAt: Date.now() });
    window.localStorage.setItem(storageKey(userId), JSON.stringify(payload));
    memory.set(String(userId), payload);
  } catch (err) {
    // QuotaExceeded — пробуем сохранить только диалоги (самое важное для UX)
    try {
      const minimal = sanitizeForStorage({
        userId,
        updatedAt: Date.now(),
        dialogs: data.dialogs ?? [],
        groups: [],
        channels: [],
        users: [],
        stickerPacks: [],
        currentUser: data.currentUser ?? null,
      });
      window.localStorage.setItem(storageKey(userId), JSON.stringify(minimal));
      memory.set(String(userId), minimal);
    } catch {
      console.warn('[chatCache] Failed to persist cache:', err?.message);
    }
  }
}

/** @returns {object|null} */
export function loadChatCache(userId) {
  if (!userId) return null;
  const key = String(userId);
  if (memory.has(key)) {
    const cached = memory.get(key);
    const age = Date.now() - (cached.updatedAt || 0);
    if (age <= MAX_AGE_MS) {
      return { ...cached, isStale: age > STALE_MS };
    }
    memory.delete(key);
  }
  const fromStorage = readFromStorage(userId);
  if (fromStorage) {
    memory.set(key, fromStorage);
    return fromStorage;
  }
  return null;
}

/** Частичное обновление кэша (после успешного API-запроса). */
export function updateChatCache(userId, partial) {
  if (!userId) return;
  const key = String(userId);
  const existing = memory.get(key) ?? readFromStorage(userId) ?? {
    userId,
    dialogs: [],
    groups: [],
    channels: [],
    users: [],
    stickerPacks: [],
    currentUser: null,
  };
  const merged = { ...existing, ...partial, userId, updatedAt: Date.now() };
  writeToStorage(userId, merged);
}

export function clearChatCache(userId) {
  if (!userId) return;
  const key = String(userId);
  memory.delete(key);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(storageKey(userId));
    } catch {
      // ignore
    }
  }
}
